import { useState } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { sledujStranku } from './lib/analytika.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { Layout } from './components/Layout.js';
import { TerenLayout, jeTerenniRole } from './components/TerenLayout.js';
import { Nacitani } from './components/ui.js';
import { LoginPage } from './pages/LoginPage.js';
import { LandingPage } from './pages/LandingPage.js';
import { FunkcePage } from './pages/FunkcePage.js';
import { ScenarePage } from './pages/ScenarePage.js';
import { RegistracePage } from './pages/RegistracePage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { VerejnyPodpisPage } from './pages/VerejnyPodpisPage.js';
import { ProtokolDetailPage } from './pages/ProtokolDetailPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ZakazkyPage } from './pages/ZakazkyPage.js';
import { ZakazkaDetailPage } from './pages/ZakazkaDetailPage.js';
import { SmlouvyPage } from './pages/SmlouvyPage.js';
import { SmlouvaNovaPage } from './pages/SmlouvaNovaPage.js';
import { SmlouvaDetailPage } from './pages/SmlouvaDetailPage.js';
import { ImportPage } from './pages/import/ImportPage.js';
import { UzivatelePage } from './pages/UzivatelePage.js';
import { VyzvyPage } from './pages/VyzvyPage.js';
import { DenikPrehledPage, DenikZaznamKancelarPage } from './pages/DenikPrehledPage.js';
import { VykresyKancelarPage } from './pages/VykresyPage.js';
import { NalezyPage } from './pages/NalezyPage.js';
import { ZmenyPage } from './pages/zmeny/ZmenyPage.js';
import { ZmenaDetailPage } from './pages/zmeny/ZmenaDetailPage.js';
import { RekapitulacePage } from './pages/RekapitulacePage.js';
import { FotogaleriePage } from './pages/FotogaleriePage.js';
import { DochazkaPrehledPage } from './pages/DochazkaPrehledPage.js';
import { DodatekDetailPage, DodatkyPage } from './pages/DodatkyPage.js';
import { DenikPage } from './pages/teren/DenikPage.js';
import { UkolyVadyPage } from './pages/teren/UkolyVadyPage.js';
import { VykresTerenDetailPage, VykresyPage } from './pages/teren/VykresyPage.js';
import { MenuPage } from './pages/teren/MenuPage.js';
import { DochazkaPage } from './pages/teren/DochazkaPage.js';
import { MojeNalezyPage } from './pages/teren/MojeNalezyPage.js';

/**
 * Dvě UI nad jedním modelem: terén (mistr — denik.write bez finance.read,
 * mobilní layout se spodní lištou) a kancelář (přípravář/admin — desktop).
 * Router je vně (BrowserRouter v main.tsx, MemoryRouter v testech).
 */

/** Kořen: nepřihlášený vidí veřejnou landing page, přihlášený jde do práce. */
function RootPresmerovani() {
  const { me, nacitani, maPravo, maFinance } = useAuth();
  if (nacitani) return <Nacitani text="Ověřuji přihlášení…" />;
  if (!me) return <LandingPage />;
  if (jeTerenniRole(maPravo, maFinance)) return <Navigate to="/teren/denik" replace />;
  // Kancelář: role za zdí financí přistane na dashboardu Přehled (B1.1),
  // ostatní (bez financí) na tabulce zakázek.
  return maFinance ? <Navigate to="/prehled" replace /> : <Navigate to="/zakazky" replace />;
}

export function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false, refetchOnWindowFocus: false },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SledovaniStranek />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/registrace" element={<RegistracePage />} />
          <Route path="/vitejte" element={<OnboardingPage />} />
          {/* VEŘEJNÁ podpisová stránka — protistrana podepisuje přes token. */}
          <Route path="/podpis/:token" element={<VerejnyPodpisPage />} />
          <Route path="/" element={<RootPresmerovani />} />
          <Route path="/funkce" element={<FunkcePage />} />
          <Route path="/scenare" element={<ScenarePage />} />

          {/* Terén (wireframe 13 §1): spodní lišta Deník / Úkoly+Vady / Výkresy / Menu. */}
          <Route path="/teren" element={<TerenLayout />}>
            <Route index element={<Navigate to="/teren/denik" replace />} />
            <Route path="denik" element={<DenikPage />} />
            <Route path="denik/:id" element={<DenikPage />} />
            <Route path="ukoly" element={<UkolyVadyPage />} />
            <Route path="vykresy" element={<VykresyPage />} />
            <Route path="vykresy/:id" element={<VykresTerenDetailPage />} />
            <Route path="vyzvy" element={<VyzvyPage />} />
            <Route path="moje-nalezy" element={<MojeNalezyPage />} />
            <Route path="dochazka" element={<DochazkaPage />} />
            <Route path="menu" element={<MenuPage />} />
          </Route>

          {/* Kancelář (desktop). */}
          <Route element={<Layout />}>
            <Route path="/prehled" element={<DashboardPage />} />
            <Route path="/zakazky" element={<ZakazkyPage />} />
            <Route path="/zakazky/:id" element={<ZakazkaDetailPage />} />
            {/* Klín (Blok D): fronta nálezů → Změny (dvě strany + marže) →
                dodatky; rekapitulace VaN per zakázka i z levé lišty. */}
            <Route path="/nalezy" element={<NalezyPage />} />
            <Route path="/zmeny" element={<ZmenyPage />} />
            <Route path="/zmeny/:id" element={<ZmenaDetailPage />} />
            <Route path="/dodatky" element={<DodatkyPage />} />
            <Route path="/dodatky/:id" element={<DodatekDetailPage />} />
            <Route path="/fotogalerie" element={<FotogaleriePage />} />
            <Route path="/dochazka" element={<DochazkaPrehledPage />} />
            <Route path="/rekapitulace" element={<RekapitulacePage />} />
            <Route path="/zakazky/:id/rekapitulace" element={<RekapitulacePage />} />
            <Route path="/denik" element={<DenikPrehledPage />} />
            <Route path="/denik/:id" element={<DenikZaznamKancelarPage />} />
            <Route path="/vykresy" element={<VykresyKancelarPage />} />
            <Route path="/vyzvy" element={<VyzvyPage />} />
            <Route path="/smlouvy" element={<SmlouvyPage />} />
            <Route path="/smlouvy/nova" element={<SmlouvaNovaPage />} />
            <Route path="/smlouvy/:id" element={<SmlouvaDetailPage />} />
            <Route path="/protokoly/:id" element={<ProtokolDetailPage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/uzivatele" element={<UzivatelePage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
}

/** SPA pageview do analytiky při každé změně routy (v produkci s klíčem). */
function SledovaniStranek() {
  const location = useLocation();
  useEffect(() => sledujStranku(), [location.pathname]);
  return null;
}
